import {
  PUBLIC_SEARCH_LANDING_SEO,
  PUBLIC_SEARCH_LANDING_SEO_SHA256,
  PUBLIC_SEARCH_LANDING_SEO_TARGET,
} from "./publicSearchLandingSeo.generated";
import {
  assertGeneratedPublicSeoTarget,
  assertGeneratedPublicSeoRoutes,
  bindGeneratedPublicSeoDataset,
  bindGeneratedPublicStorageUrl,
} from "./generatedPublicStorageUrls";

assertGeneratedPublicSeoTarget(PUBLIC_SEARCH_LANDING_SEO_TARGET);

const PUBLIC_SEARCH_LANDING_SEO_DATASET = bindGeneratedPublicSeoDataset(
  "searchLandingRoutes",
  PUBLIC_SEARCH_LANDING_SEO,
  PUBLIC_SEARCH_LANDING_SEO_SHA256
);

export type PublicSearchLandingBusiness = {
  name: string;
  slug: string;
  description: string | null;
  categoryName: string | null;
  subcategoryName: string | null;
  countyName: string | null;
  cityName: string | null;
  areaName: string | null;
  logoUrl: string | null;
  itemNames: string[];
};

export type PublicSearchLanding = {
  slug: string;
  queryLabel: string;
  locationLabel: string;
  businesses: PublicSearchLandingBusiness[];
};

const PUBLIC_SEARCH_LANDINGS =
  PUBLIC_SEARCH_LANDING_SEO_DATASET as unknown as PublicSearchLanding[];

function cleanString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function cleanNullableString(value: unknown): string | null {
  const clean = cleanString(value);
  return clean || null;
}

function cleanStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value.map(cleanString).filter(Boolean);
}

function cleanBusiness(value: unknown): PublicSearchLandingBusiness | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const row = value as Record<string, unknown>;
  const name = cleanString(row.name);
  const slug = cleanString(row.slug);

  if (!name || !slug) return null;

  return {
    name,
    slug,
    description: cleanNullableString(row.description),
    categoryName: cleanNullableString(row.categoryName),
    subcategoryName: cleanNullableString(row.subcategoryName),
    countyName: cleanNullableString(row.countyName),
    cityName: cleanNullableString(row.cityName),
    areaName: cleanNullableString(row.areaName),
    logoUrl: bindGeneratedPublicStorageUrl(
      cleanNullableString(row.logoUrl),
      PUBLIC_SEARCH_LANDING_SEO_TARGET
    ),
    itemNames: cleanStringArray(row.itemNames),
  };
}

function cleanLanding(value: unknown): PublicSearchLanding | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const row = value as Record<string, unknown>;
  const slug = cleanString(row.slug);
  const queryLabel = cleanString(row.queryLabel);
  const locationLabel = cleanString(row.locationLabel);
  const businesses = Array.isArray(row.businesses)
    ? row.businesses.flatMap((item) => {
        const business = cleanBusiness(item);
        return business ? [business] : [];
      })
    : [];

  if (!slug || !queryLabel || !locationLabel || businesses.length === 0) {
    return null;
  }

  return {
    slug,
    queryLabel,
    locationLabel,
    businesses,
  };
}

export function getGeneratedPublicSearchLandings(): PublicSearchLanding[] {
  return PUBLIC_SEARCH_LANDINGS.flatMap((item) => {
    const landing = cleanLanding(item);
    return landing ? [landing] : [];
  }).sort((a, b) => a.slug.localeCompare(b.slug));
}

export function getGeneratedPublicSearchLandingSlugs(): string[] {
  return getGeneratedPublicSearchLandings().map((landing) => landing.slug);
}

export function getGeneratedPublicSearchLanding(
  slug?: string | null
): PublicSearchLanding | null {
  const cleanSlug = cleanString(slug);
  if (!cleanSlug) return null;

  return (
    getGeneratedPublicSearchLandings().find(
      (landing) => landing.slug === cleanSlug
    ) ?? null
  );
}

export function buildPublicSearchLandingProfilePath(slug: string): string {
  return `/p/${encodeURIComponent(slug)}`;
}

assertGeneratedPublicSeoRoutes(
  "searchLandingRoutes",
  getGeneratedPublicSearchLandingSlugs().map(
    (slug) => `/s/${encodeURIComponent(slug)}`
  ),
  PUBLIC_SEARCH_LANDING_SEO_TARGET
);
